import React from 'react';
import _ from 'lodash';
import {submit, isSubmitting, reset} from 'redux-form';

import {default as Modal, withModal} from 'components/Modal';
import {UserBaseDetails} from 'components/UserDetails';
import AvatarUpload from 'components/AvatarUpload';
import {Buttons, UserEditForm, IsAdmin, IsMember} from './components';

import {
  MODALS,
  fetchUser,
  promoteToMember,
  demoteMember,
  fetchUsers,
  verifyUserInternalAvatarUpload
} from 'ducks/users';
import {selectModal, storeDataInModal} from 'ducks/modals';
import {fetchCountries} from 'ducks/countries';
import {requiresPermission, PERMISSIONS, userProfileRequest} from 'ducks/auth';
import {uploadFile} from 'ducks/files';
import {delayedAction} from 'ducks/common';

import {signUserInternalAvatar} from 'sdk/users';

import {headOption} from 'utils/common';
import {getAuthToken} from 'utils/localStorage';

import './styles.css';

// This delay is needed because the call to S3 is slower than the fetch we make again
const REFETCH_AVATARS_DELAY = 500;

class UserDetailModal extends React.Component<any> {
  state = {
    wrongFormat: false
  };

  componentDidMount() {
    this.fetchUser();

    this.props.fetchCountries();
  }

  fetchUser = () => {
    const extraActions = [
      ...this.props.storeDataExtraActions(data => ({
        user: data
      }))
    ];

    this.props.fetchUser({userId: this.props.data.userId}, extraActions);
  };

  turnOnEditMode = () =>
    this.props.requiresPermission({permission: PERMISSIONS.EDIT_USERS}, () =>
      this.props.storeDataInModal({
        modalName: MODALS.USER_DETAIL,
        data: {
          isInEditMode: true
        }
      })
    );

  turnOffEditMode = () => {
    this.props.storeDataInModal({
      modalName: MODALS.USER_DETAIL,
      data: {
        isInEditMode: false
      }
    });
    this.props.reset('updateUser');
  };

  promoteToMember = userId => {
    const {closeModalExtraActions} = this.props;

    const extraActions = [...closeModalExtraActions, () => fetchUsers()];

    this.props.requiresPermission({permission: PERMISSIONS.EDIT_USERS}, () => {
      this.props.promoteToMember({userId}, extraActions);
    });
  };

  demoteMember = memberId => {
    const {closeModalExtraActions} = this.props;

    const extraActions = [...closeModalExtraActions, () => fetchUsers()];

    this.props.requiresPermission({permission: PERMISSIONS.EDIT_USERS}, () => {
      this.props.demoteMember({memberId}, extraActions);
    });
  };

  triggerSubmit = () => this.props.submit('updateUser');

  closeModal = () => {
    this.props.closeModal();

    const token = getAuthToken();
    this.props.userProfileRequest(token);
  };

  onFileDrop = files => {
    if (_.isEmpty(files)) {
      return this.setState({wrongFormat: true});
    }

    const {data: {user}} = this.props;
    const userId = user.id;

    const file = headOption(files);
    const signData = {
      filename: file.name,
      fileType: file.type,
      userId
    };
    const finishUploadingAction = () =>
      delayedAction({
        delay: REFETCH_AVATARS_DELAY,
        action: () =>
          fetchUser({userId}, [
            ...this.props.storeDataExtraActions(user => ({user})),
            fetchUsers
          ])
      });

    const storeIdentifierAction = identifier =>
      verifyUserInternalAvatarUpload({identifier, userId});

    this.props.uploadFile({
      file,
      signSdk: signUserInternalAvatar,
      signData,
      finishUploadingAction,
      storeIdentifierAction
    });

    this.setState({wrongFormat: false});
  };

  render() {
    const {wrongFormat} = this.state;
    const {data: {user, form: {submitting}, isInEditMode}} = this.props;

    return (
      <Modal
        show={true}
        onClose={this.closeModal}
        type={Modal.EXTRA_SMALL}
        className="UserDetailModal">
        <Modal.Header heading="User information" />
        <Modal.Body>
          {user && (
            <div>
              <UserBaseDetails user={user} />
              <AvatarUpload
                user={user}
                onFileDrop={this.onFileDrop}
                wrongFormat={wrongFormat}
              />
              <UserEditForm
                isInEditMode={isInEditMode}
                user={user}
                storeDataExtraActions={this.props.storeDataExtraActions}
              />
              <IsAdmin isAdmin={user.isAdmin} />
              <IsMember isMember={user.isMember} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Buttons
            user={user}
            promoteToMember={this.promoteToMember}
            demoteMember={this.demoteMember}
            isInEditMode={isInEditMode}
            turnOnEditMode={this.turnOnEditMode}
            turnOffEditMode={this.turnOffEditMode}
            triggerSubmit={this.triggerSubmit}
            submitting={submitting}
          />
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToModalData = state => {
  const {data: {isInEditMode}} = selectModal(state, MODALS.USER_DETAIL);

  return {
    form: {
      submitting: isSubmitting('updateUser')(state)
    },
    isInEditMode: isInEditMode || false
  };
};

const mapDispatchToProps = {
  fetchUser,
  promoteToMember,
  demoteMember,
  fetchCountries,
  submit,
  reset,
  storeDataInModal,
  uploadFile,
  requiresPermission,
  userProfileRequest
};

export default withModal(
  MODALS.USER_DETAIL,
  mapStateToModalData,
  mapDispatchToProps
)(UserDetailModal);
